// src/client/components/layout/sidebar.tsx
import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { cn } from "@client/lib/utils";
import { useAppInfo } from "@client/hooks/use-settings";
import {
  Home,
  List,
  User,
  Users,
  Database,
  Settings,
  Cog,
  ChevronDown,
  ChevronRight,
  Palette,
  Sliders,
  FileCode,
  Target,
  Code,
  Download,
  MessageSquare
} from "lucide-react";

interface SidebarProps {}

export function Sidebar({}: SidebarProps) {
  const location = useLocation();
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const { name: appName, version: appVersion } = useAppInfo();

  // Auto-expand settings if on settings page
  useEffect(() => {
    if (location.pathname === '/settings' || location.pathname.startsWith('/settings')) {
      setSettingsExpanded(true);
    }
  }, [location]);

  // Main Navigation Items
  const navItems = [
    { path: "/", label: "Dashboard", icon: Home },
  ];

  // Management Items - Based on actual routes in App.tsx
  const managementItems = [
    { path: "/lists", label: "List Manager", icon: List },
    { path: "/conversations", label: "Conversations", icon: MessageSquare },
    { path: "/profile-manager", label: "Profile Manager", icon: User },
    { path: "/userscript", label: "Userscript Download", icon: Download },
    { path: "/tbd", label: "Todos", icon: Code },
    { path: "/users", label: "Users", icon: Users },
    { path: "/admin", label: "DB Admin", icon: Database },
  ];

  // Settings Sub-Navigation Items
  const settingsSubItems = [
    { path: "/settings?tab=app", label: "Application", icon: Cog },
    { path: "/settings?tab=features", label: "Features", icon: Sliders },
    { path: "/settings?tab=categories", label: "Categories", icon: Palette },
    { path: "/settings?tab=scores", label: "Score Ranges", icon: Target },
    { path: "/settings?tab=selectors", label: "Selectors", icon: FileCode },
    { path: "/settings?tab=xpath-definitions", label: "XPath Config", icon: FileCode },
    { path: "/settings?tab=data-values", label: "Data Values", icon: Database },
    { path: "/settings?tab=system", label: "System", icon: Database },
  ];

  const isSettingsActive = location.pathname === '/settings' || location.pathname.startsWith('/settings');

  return (
    <div className="w-64 bg-white shadow-sm border-r border-slate-200 flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Cog className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-800">
              {appName || 'TM Application'}
            </h1>
            <p className="text-xs text-slate-500">v{appVersion || '2.0.0'}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {/* Main Navigation */}
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;

          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex items-center space-x-3 px-3 py-2 rounded-lg font-medium transition-colors",
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-slate-700 hover:bg-slate-100",
              )}
            >
              <Icon className="w-5 h-5" />
              <span>{item.label}</span>
            </Link>
          );
        })}

        {/* Management Section */}
        <div className="pt-4">
          <h3 className="px-3 text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
            Management
          </h3>

          {managementItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-slate-700 hover:bg-slate-100",
                )}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>

        {/* Settings with Sub-Navigation */}
        <div className="pt-4">
          <div className="space-y-1">
            {/* Main Settings Toggle */}
            <button
              onClick={() => setSettingsExpanded(!settingsExpanded)}
              className={cn(
                "w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors",
                isSettingsActive
                  ? "bg-primary/10 text-primary"
                  : "text-slate-700 hover:bg-slate-100",
              )}
            >
              <div className="flex items-center space-x-3">
                <Settings className="w-5 h-5" />
                <span>Settings</span>
              </div>
              {settingsExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>

            {/* Settings Sub-Navigation */}
            {settingsExpanded && (
              <div className="ml-4 space-y-1 border-l-2 border-slate-200 pl-2">
                {settingsSubItems.map((item) => {
                  const Icon = item.icon;
                  // Check if current URL matches the tab parameter
                  const searchParams = new URLSearchParams(location.search);
                  const currentTab = searchParams.get('tab');
                  const itemTab = new URLSearchParams(item.path.split('?')[1]).get('tab');
                  const isActive = location.pathname === '/settings' && currentTab === itemTab;

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cn(
                        "flex items-center space-x-2 px-2 py-1.5 rounded text-sm transition-colors",
                        isActive
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-slate-600 hover:bg-slate-100",
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </nav>
    </div>
  );
}